import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import * as XLSX from 'xlsx-js-style';
import { useSelector } from 'react-redux';
import { getMonthList } from '../../services/monthList';
import { getYearList } from '../../services/yearList';
import { getStoredCompanyDetails, getCompanyDisplayFromDetails, getShortCompanyName } from '../../services/companyService';
import Notification from '../Notification';

const monthOptions = getMonthList();
const yearOptions = getYearList();

const today = new Date();
const currentYear = today.getFullYear().toString();
const currentMonthIndex = today.getMonth();
const currentMonthName = monthOptions[currentMonthIndex].title;

const initialFValues = {
  month: currentMonthName,
  year: currentYear,
};

const RequiredLabel = ({ label }) => (
  <span>
    {label}
    <span style={{ color: 'red', marginLeft: '3px' }}>*</span>
  </span>
);

function StipendUploadFile({ isEmbedded = false }) {
  const [values, setValues] = useState(initialFValues);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [notify, setNotify] = useState({ isOpen: false, message: '', type: '' });
  const [rows, setRows] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [creditNarration, setCreditNarration] = useState('');
  const [debitNarration, setDebitNarration] = useState('');
  const [debitAccountNo, setDebitAccountNo] = useState('');
  const [internetBankingId, setInternetBankingId] = useState('');
  const [activeTab, setActiveTab] = useState('icici');
  const companyDetails = useSelector((state) => state.auth.activeCompanyDetails) || getStoredCompanyDetails();
  const { companyName: globalCompanyName, addressLine1: globalAddressLine1, addressLine2: globalAddressLine2 } =
    getCompanyDisplayFromDetails(companyDetails || {});

  const canShowTable = useMemo(() => Array.isArray(rows) && rows.length > 0, [rows]);
  const totalSelectedAmount = useMemo(() => {
    const set = new Set(selectedRowKeys);
    return rows
      .filter((r) => set.has(r.__rowKey))
      .reduce((sum, r) => sum + Math.round(Number(r.prorated_pay || 0)), 0);
  }, [rows, selectedRowKeys]);

  // Reset selection when month/year changes
  useEffect(() => {
    setSelectedRowKeys([]);
  }, [values.month, values.year]);

  const validate = () => {
    const temp = {};
    temp.month = values.month ? '' : 'This field is required.';
    temp.year = values.year ? '' : 'This field is required.';
    setErrors(temp);
    return Object.values(temp).every((x) => x === '');
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setValues((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleFetch = async () => {
    if (!validate()) return;
    setLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/apprentice-master/month-wise-apprentice/${values.month}/${values.year}`
      );
      const raw = response.data;

      // Expected new format:
      // { internet_banking_id: string, bank_account_number: string, employees: [...] }
      // Fallbacks: array response OR {data: [...]}
      const employees = Array.isArray(raw)
        ? raw
        : Array.isArray(raw?.employees)
          ? raw.employees
          : Array.isArray(raw?.data)
            ? raw.data
            : [];

      const mapped = employees.map((emp, index) => {
        const fallbackKey = emp?.employee_code || emp?.bank_account_number || `${emp?.first_name || ''}_${emp?.last_name || ''}_${index}`;
        return { ...emp, __rowKey: String(fallbackKey) };
      });

      setDebitAccountNo(raw?.bank_account_number || '');
      setInternetBankingId(raw?.internet_banking_id || '');
      setRows(mapped);
    } catch (e) {
      setRows([]);
      setNotify({
        isOpen: true,
        message: e.response?.data?.message || 'Failed to fetch stipend upload data',
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const cleanDisplayValue = (value) => {
    if (value === null || value === undefined || value === '') return '';
    let cleaned = String(value);
    cleaned = cleaned.replace(/'/g, '');
    cleaned = cleaned.replace(/[''""]/g, '');
    cleaned = cleaned.trim();
    return cleaned;
  };

  const getLast4Digits = (accountNumber) => {
    if (!accountNumber) return '0000';
    const cleanedAccount = cleanDisplayValue(accountNumber);
    return cleanedAccount.slice(-4).padStart(4, '0');
  };

  const getFirst4Digits = (accountNumber) => {
    if (!accountNumber) return '0000';
    const cleanedAccount = cleanDisplayValue(accountNumber);
    return cleanedAccount.substring(0, 4).padEnd(4, '0');
  };

  const padRight = (str, length) => {
    const strValue = String(str || '');
    const truncated = strValue.length > length ? strValue.substring(0, length) : strValue;
    return truncated.padEnd(length, ' ');
  };

  const getOrdinalSuffix = (num) => {
    if (num === 1) return '1st';
    if (num === 2) return '2nd';
    if (num === 3) return '3rd';
    return `${num}th`;
  };

  const handleSelectEmployee = (employeeCode) => {
    if (!employeeCode) return;
    setSelectedRowKeys((prev) => {
      const next = prev.includes(employeeCode) ? prev.filter((c) => c !== employeeCode) : [...prev, employeeCode];
      const set = new Set(next);
      const total = rows
        .filter((r) => set.has(r.__rowKey))
        .reduce((sum, r) => sum + Math.round(Number(r.prorated_pay || 0)), 0);
      if (total > 1000000) {
        setNotify({
          isOpen: true,
          message: 'The sum of stipend of selected employees cannot exceed 10 lakhs!',
          type: 'error',
        });
        return prev;
      }
      return next;
    });
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      const allCodes = rows.map((r) => r.__rowKey).filter(Boolean);
      const total = rows.reduce((sum, r) => sum + Math.round(Number(r.prorated_pay || 0)), 0);
      if (total > 1000000) {
        setNotify({
          isOpen: true,
          message: 'The sum of stipend of selected employees cannot exceed 10 lakhs!',
          type: 'error',
        });
        return;
      }
      setSelectedRowKeys(allCodes);
    } else {
      setSelectedRowKeys([]);
    }
  };

  const handleGenerate = () => {
    if (selectedRowKeys.length === 0) {
      setNotify({ isOpen: true, message: 'Please select at least one employee!', type: 'error' });
      return;
    }
    if (totalSelectedAmount > 1000000) {
      setNotify({ isOpen: true, message: 'The sum of stipend of selected employees cannot exceed 10 lakhs!', type: 'error' });
      return;
    }
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setCreditNarration('');
    setDebitNarration('');
  };

  const downloadNotepadFile = (selectedRows) => {
    const totalAmount = selectedRows.reduce((sum, emp) => sum + Math.round(Number(emp.prorated_pay || 0)), 0);

    let textContent = '';
    textContent += `${internetBankingId || '-'}\n`;

    // Formula-based formatting:
    // TEXT(account,"000000000000") & REPT(" ",4) & "INR" & LEFT(account,4) & REPT(" ",4) & "D/C" & REPT(" ",15-LEN(amount)) & amount & narration
    const formatAccount12 = (accountNumber) => {
      if (!accountNumber) return '000000000000';
      const cleaned = cleanDisplayValue(accountNumber);
      return cleaned.padStart(12, '0').substring(0, 12);
    };
    const formatAmount15 = (amount) => String(amount).padStart(15, ' ');

    const first4Digits = getFirst4Digits(debitAccountNo);
    const formattedBankAccount = formatAccount12(debitAccountNo);
    const debitInrCode = `INR${first4Digits}`;
    const debitAmount = totalAmount.toFixed(2);

    textContent += `${formattedBankAccount}    ${debitInrCode}    D${formatAmount15(debitAmount)}${debitNarration}\n`;

    selectedRows.forEach((emp) => {
      const empFirst4 = getFirst4Digits(emp.bank_account_number);
      const empAccount = formatAccount12(emp.bank_account_number);
      const creditInrCode = `INR${empFirst4}`;
      const creditAmount = Math.round(Number(emp.prorated_pay || 0)).toFixed(2);
      textContent += `${empAccount}    ${creditInrCode}    C${formatAmount15(creditAmount)}${creditNarration}\n`;
    });

    const monthAbbr = values.month.substring(0, 3);
    const yearShort = String(values.year).slice(-2);
    const storageKey = `stipend_statement_counter_${monthAbbr}${yearShort}`;
    const counter = parseInt(localStorage.getItem(storageKey) || '1', 10);
    const filename = `${getOrdinalSuffix(counter)}upld${monthAbbr}${yearShort}Stipend.txt`;
    localStorage.setItem(storageKey, (counter + 1).toString());

    const blob = new Blob([textContent], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const getFormattedDate = () => {
    const now = new Date();
    const day = String(now.getDate()).padStart(2, '0');
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const year = now.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const downloadExcelFile = (selectedRows) => {
    // Excel design should match Salary (Bank Statement) IFSC Excel exactly
    const numCols = 19;
    const excelData = [];

    excelData.push([
      'PYMT_PROD_TYPE_CODE',
      'PYMT_MODE',
      'DEBIT_ACC_NO',
      'BNF_NAME',
      'BENE_ACC_NO',
      'BENE_IFSC',
      'AMOUNT',
      'DEBIT_NARR',
      'CREDIT_NARR',
      'MOBILE_NUM',
      'EMAIL_ID',
      'REMARK',
      'PYMT_DATE',
      'REF_NO',
      'ADDL_INFO1',
      'ADDL_INFO2',
      'ADDL_INFO3',
      'ADDL_INFO4',
      'LEI_NUMBER',
    ]);

    const currentDate = getFormattedDate();

    selectedRows.forEach((emp) => {
      const fullName = (emp.name || `${emp.first_name || ''} ${emp.last_name || ''}`.trim()).trim();
      excelData.push([
        'PAB_VENDOR',
        'NEFT',
        debitAccountNo || '',
        fullName,
        cleanDisplayValue(emp.bank_account_number || ''),
        emp.ifsc_code || '',
        Math.round(Number(emp.prorated_pay || 0)),
        debitNarration,
        creditNarration,
        '',
        '',
        '',
        currentDate,
        '',
        '',
        '',
        '',
        '',
        '',
      ]);
    });

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(excelData);

    const calculateColumnWidths = (data) => {
      const colWidths = [];
      const cols = data[0]?.length || 19;
      for (let col = 0; col < cols; col++) {
        let maxWidth = 0;
        data.forEach((row, rowIndex) => {
          if (row[col] !== undefined && row[col] !== null) {
            maxWidth = Math.max(maxWidth, String(row[col]).length);
          }
        });
        colWidths.push({ wch: maxWidth + 1 });
      }
      return colWidths;
    };

    ws['!cols'] = calculateColumnWidths(excelData);

    ws['!merges'] = [];

    const borderStyle = {
      top: { style: 'thin', color: { rgb: '000000' } },
      bottom: { style: 'thin', color: { rgb: '000000' } },
      left: { style: 'thin', color: { rgb: '000000' } },
      right: { style: 'thin', color: { rgb: '000000' } },
    };

    const redHeaderStyle = {
      font: { color: { rgb: 'FF0000' }, bold: true },
      fill: { fgColor: { rgb: '5B9BD5' } },
      alignment: { horizontal: 'center', vertical: 'center' },
      border: borderStyle,
    };

    const blueHeaderStyle = {
      font: { bold: true, color: { rgb: 'FFFFFF' } },
      fill: { fgColor: { rgb: '0B418E' } },
      alignment: { horizontal: 'center', vertical: 'center' },
      border: borderStyle,
    };

    // Column header row is now row 1 (A1..)
    const redHeaderCells = ['A1', 'B1', 'C1', 'D1', 'E1', 'F1', 'G1', 'M1'];
    redHeaderCells.forEach((cell) => {
      if (ws[cell]) ws[cell].s = redHeaderStyle;
    });

    const blueHeaderCells = ['H1', 'I1', 'J1', 'K1', 'L1', 'N1', 'O1', 'P1', 'Q1', 'R1', 'S1'];
    blueHeaderCells.forEach((cell) => {
      if (ws[cell]) ws[cell].s = blueHeaderStyle;
    });

    const range = XLSX.utils.decode_range(ws['!ref']);
    // Data rows start from row 2
    for (let row = 1; row <= range.e.r; row++) {
      for (let col = 0; col < 19; col++) {
        const cellAddress = XLSX.utils.encode_cell({ r: row, c: col });
        if (ws[cellAddress]) {
          ws[cellAddress].s = { border: borderStyle, alignment: { vertical: 'center', horizontal: 'left' } };
          // AMOUNT column (G) with comma-separated format
          if (col === 6) {
            ws[cellAddress].z = '#,##0';
          }
        }
      }
    }

    XLSX.utils.book_append_sheet(wb, ws, 'Bank Statement');
    const filename = `${getShortCompanyName(globalCompanyName) || 'Report'} Report for Bank Statement ${values.month.substring(0, 3)} ${values.year}.xlsx`;
    XLSX.writeFile(wb, filename);
  };

  const handleSave = () => {
    if (!creditNarration.trim() || !debitNarration.trim()) {
      setNotify({ isOpen: true, message: 'Please fill in both credit and debit narration!', type: 'error' });
      return;
    }

    const selectedSet = new Set(selectedRowKeys);
    const selectedRows = rows.filter((r) => selectedSet.has(r.__rowKey));

    const ifscRows = selectedRows.filter((r) => r.is_ifsc === true);
    const nonIfscRows = selectedRows.filter((r) => r.is_ifsc === false);

    if (nonIfscRows.length > 0) {
      downloadNotepadFile(nonIfscRows);
    }
    if (ifscRows.length > 0) {
      downloadExcelFile(ifscRows);
    }

    setNotify({ isOpen: true, message: 'Files generated successfully!', type: 'success' });
    setSelectedRowKeys([]);
    handleCloseModal();
  };

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', padding: isEmbedded ? '10px 20px' : '30px' }}>
      <Paper elevation={0} sx={{ width: '1000px' }}>
        <Stack spacing={2} sx={{ margin: isEmbedded ? '10px 10px' : '30px' }}>
          <Stack direction="row" spacing={2} justifyContent="center" flexWrap="wrap">
            <FormControl variant="outlined" sx={{ width: '200px' }} {...(errors.month && { error: true })}>
              <InputLabel>
                <RequiredLabel label="Month" />
              </InputLabel>
              <Select label="Month" name="month" size="small" value={values.month} onChange={handleInputChange}>
                <MenuItem value="">None</MenuItem>
                {monthOptions.map((item) => (
                  <MenuItem key={item.id} value={item.title}>
                    {item.title}
                  </MenuItem>
                ))}
              </Select>
              {errors.month && <FormHelperText>{errors.month}</FormHelperText>}
            </FormControl>

            <FormControl variant="outlined" sx={{ width: '200px' }} {...(errors.year && { error: true })}>
              <InputLabel>
                <RequiredLabel label="Year" />
              </InputLabel>
              <Select label="Year" name="year" size="small" value={values.year} onChange={handleInputChange}>
                <MenuItem value="">None</MenuItem>
                {yearOptions.map((item) => (
                  <MenuItem key={item.id} value={item.title}>
                    {item.title}
                  </MenuItem>
                ))}
              </Select>
              {errors.year && <FormHelperText>{errors.year}</FormHelperText>}
            </FormControl>

            <Button style={{ height: 40 }} variant="outlined" color="secondary" onClick={handleFetch} disabled={loading}>
              {loading ? <CircularProgress size={24} color="secondary" /> : 'Display'}
            </Button>
          </Stack>

          {canShowTable ? ((() => {
              const iciciRows = rows.filter(r => r.is_ifsc === false);
              const nonIciciRows = rows.filter(r => r.is_ifsc === true);
              const iciciKeys = iciciRows.map(r => r.__rowKey);
              const nonIciciKeys = nonIciciRows.map(r => r.__rowKey);
              const selectedSet = new Set(selectedRowKeys);
              const getGroupTotal = (grpRows) => grpRows.reduce((sum, r) => sum + Math.round(Number(r.prorated_pay || 0)), 0);
              const selectedIciciAmount = iciciRows.filter(r => selectedSet.has(r.__rowKey)).reduce((sum, r) => sum + Math.round(Number(r.prorated_pay || 0)), 0);
              const selectedNonIciciAmount = nonIciciRows.filter(r => selectedSet.has(r.__rowKey)).reduce((sum, r) => sum + Math.round(Number(r.prorated_pay || 0)), 0);
              const selectedIciciCount = iciciKeys.filter(k => selectedSet.has(k)).length;
              const selectedNonIciciCount = nonIciciKeys.filter(k => selectedSet.has(k)).length;
              const currentRows = activeTab === 'icici' ? iciciRows : nonIciciRows;
              const currentKeys = currentRows.map(r => r.__rowKey);
              const allCurrentSelected = currentKeys.length > 0 && currentKeys.every(k => selectedSet.has(k));
              const someCurrentSelected = currentKeys.some(k => selectedSet.has(k));

              const handleSelectAllTab = (event) => {
                if (event.target.checked) {
                  const available = currentKeys.filter(Boolean);
                  const newSelection = Array.from(new Set([...selectedRowKeys, ...available]));
                  const total = rows.filter(r => new Set(newSelection).has(r.__rowKey)).reduce((sum, r) => sum + Math.round(Number(r.prorated_pay || 0)), 0);
                  if (activeTab === 'icici' && total > 1000000) {
                    setNotify({ isOpen: true, message: 'The sum of stipend of selected employees cannot exceed 10 lakhs!', type: 'error' });
                    return;
                  }
                  setSelectedRowKeys(newSelection);
                } else {
                  setSelectedRowKeys(prev => prev.filter(k => !currentKeys.includes(k)));
                }
              };

              const handleTabGenerate = () => {
                const tabSelected = selectedRowKeys.filter(k => currentKeys.includes(k));
                if (tabSelected.length === 0) {
                  setNotify({ isOpen: true, message: 'Please select at least one employee!', type: 'error' });
                  return;
                }
                const total = rows.filter(r => new Set(tabSelected).has(r.__rowKey)).reduce((sum, r) => sum + Math.round(Number(r.prorated_pay || 0)), 0);
                if (activeTab === 'icici' && total > 1000000) {
                  setNotify({ isOpen: true, message: 'The sum of stipend of selected employees cannot exceed 10 lakhs!', type: 'error' });
                  return;
                }
                setOpenModal(true);
              };

              return (
                <Box sx={{ width: '100%', background: '#ffffff', borderRadius: 2, border: '1px solid #e9ecef', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', display: 'flex', flexDirection: 'column', height: 'calc(100vh - 220px)', overflow: 'hidden' }}>
                  {/* ── Sticky Summary Boxes ── */}
                  <Box sx={{ flexShrink: 0, backgroundColor: '#ffffff', px: 2, pt: 2, pb: 1, borderBottom: '2px solid #dee2e6', boxShadow: '0 2px 6px rgba(0,0,0,0.08)', display: 'flex', justifyContent: 'space-between', alignItems: 'stretch', flexWrap: 'wrap', gap: 2 }}>
                    {/* ICICI */}
                    <Box sx={{ flex: 1, minWidth: '200px', border: '1px solid #b8d4f0', borderRadius: 1, p: 1.5, backgroundColor: '#eaf4fb' }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1f4e79' }}>
                        ICICI — {iciciRows.length} Employees
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.5, color: '#495057', fontWeight: 600 }}>
                        Total Stipend: ₹{getGroupTotal(iciciRows).toLocaleString('en-IN')}
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.25, fontWeight: 700, color: selectedIciciAmount > 1000000 ? '#d32f2f' : '#1b7a3e' }}>
                        Selected: {selectedIciciCount} &nbsp;|&nbsp; ₹{selectedIciciAmount.toLocaleString('en-IN')}
                      </Typography>
                      <Typography variant="caption" sx={{ mt: 0.25, display: 'block', color: selectedIciciAmount > 1000000 ? '#d32f2f' : '#6c757d', fontWeight: 600 }}>
                        Limit: ₹10,00,000
                      </Typography>
                    </Box>
                    {/* Non-ICICI */}
                    <Box sx={{ flex: 1, minWidth: '200px', border: '1px solid #c3e6cb', borderRadius: 1, p: 1.5, backgroundColor: '#f0fbf4' }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1f4e79' }}>
                        Non-ICICI — {nonIciciRows.length} Employees
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.5, color: '#495057', fontWeight: 600 }}>
                        Total Stipend: ₹{getGroupTotal(nonIciciRows).toLocaleString('en-IN')}
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.25, fontWeight: 700, color: '#1b7a3e' }}>
                        Selected: {selectedNonIciciCount} &nbsp;|&nbsp; ₹{selectedNonIciciAmount.toLocaleString('en-IN')}
                      </Typography>
                    </Box>
                    {/* Grand Total */}
                    <Box sx={{ flex: 1, minWidth: '200px', border: '2px solid #f0ad4e', borderRadius: 1, p: 1.5, backgroundColor: '#fffbf0' }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#7d4e00' }}>
                        Grand Total — {rows.length} Employees
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.5, color: '#495057', fontWeight: 600 }}>
                        Total Stipend: ₹{(getGroupTotal(iciciRows) + getGroupTotal(nonIciciRows)).toLocaleString('en-IN')}
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.25, fontWeight: 700, color: '#7d4e00' }}>
                        Selected: {selectedIciciCount + selectedNonIciciCount} &nbsp;|&nbsp; ₹{(selectedIciciAmount + selectedNonIciciAmount).toLocaleString('en-IN')}
                      </Typography>
                    </Box>
                  </Box>

                  {/* ── Tabs ── */}
                  <Box sx={{ flexShrink: 0, px: 2, pt: 1, borderBottom: '1px solid #dee2e6', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                    <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} sx={{ minHeight: '40px', '& .MuiTab-root': { minHeight: '40px', textTransform: 'none', fontWeight: 600 } }}>
                      <Tab label={`ICICI (${iciciRows.length})`} value="icici" />
                      <Tab label={`Non-ICICI (${nonIciciRows.length})`} value="nonIcici" />
                    </Tabs>
                    <Typography variant="body2" sx={{ color: '#6c757d', fontWeight: 500 }}>
                      Showing: {currentRows.length} employees
                    </Typography>
                  </Box>

                  {/* ── Scrollable Table ── */}
                  <div style={{ flex: 1, width: '100%', overflow: 'auto', border: '1px solid #dee2e6', backgroundColor: '#fff' }}>
                    <Table size="small" sx={{ width: '100%', borderCollapse: 'collapse' }}>
                      <TableHead style={{ position: 'sticky', top: 0, zIndex: 10, background: '#212529', color: 'white', boxShadow: '0 2px 2px -1px rgba(0,0,0,0.2)' }}>
                        <TableRow>
                          <TableCell padding="checkbox" sx={{ width: '48px', color: 'white', borderBottom: '1px solid #dee2e6', padding: '12px 8px' }}>
                            <Checkbox size="small" checked={allCurrentSelected} indeterminate={someCurrentSelected && !allCurrentSelected} onChange={handleSelectAllTab} sx={{ color: 'rgba(255,255,255,0.8)', '&.Mui-checked': { color: 'white' } }} />
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600, color: 'white', borderBottom: '1px solid #dee2e6', padding: '12px 8px' }}>Intern/Apprentice Code</TableCell>
                          <TableCell sx={{ fontWeight: 600, color: 'white', borderBottom: '1px solid #dee2e6', padding: '12px 8px' }}>Name</TableCell>
                          <TableCell sx={{ fontWeight: 600, color: 'white', borderBottom: '1px solid #dee2e6', padding: '12px 8px' }}>Account No</TableCell>
                          <TableCell sx={{ fontWeight: 600, color: 'white', borderBottom: '1px solid #dee2e6', padding: '12px 8px' }}>IFSC</TableCell>
                          <TableCell sx={{ fontWeight: 600, color: 'white', borderBottom: '1px solid #dee2e6', padding: '12px 8px', textAlign: 'right' }}>Net Pay</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {currentRows.map((employee, index) => (
                          <TableRow key={employee.__rowKey || employee.employee_code || index} hover sx={{ backgroundColor: 'white' }}>
                            <TableCell padding="checkbox" sx={{ borderBottom: '1px solid #dee2e6', padding: '12px 8px' }}>
                              <Checkbox size="small" checked={selectedRowKeys.includes(employee.__rowKey)} onChange={() => handleSelectEmployee(employee.__rowKey)} />
                            </TableCell>
                            <TableCell sx={{ borderBottom: '1px solid #dee2e6', padding: '12px 8px' }}>{employee.employee_code || '-'}</TableCell>
                            <TableCell sx={{ borderBottom: '1px solid #dee2e6', padding: '12px 8px' }}>{employee.name || `${employee.first_name || ''} ${employee.last_name || ''}`.trim()}</TableCell>
                            <TableCell sx={{ borderBottom: '1px solid #dee2e6', padding: '12px 8px' }}>{cleanDisplayValue(employee.bank_account_number || '')}</TableCell>
                            <TableCell sx={{ borderBottom: '1px solid #dee2e6', padding: '12px 8px' }}>{employee.ifsc_code || '-'}</TableCell>
                            <TableCell sx={{ borderBottom: '1px solid #dee2e6', padding: '12px 8px', textAlign: 'right' }}>₹{Math.round(Number(employee.prorated_pay || 0)).toLocaleString('en-IN')}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>

                  {/* ── Fixed Footer Generate Button ── */}
                  <Box sx={{ flexShrink: 0, backgroundColor: '#ffffff', borderTop: '2px solid #dee2e6', boxShadow: '0 -2px 6px rgba(0,0,0,0.08)', display: 'flex', justifyContent: 'center', py: 1.5 }}>
                    <Button color="primary" variant="contained" size="large" onClick={handleTabGenerate} disabled={selectedRowKeys.filter(k => currentKeys.includes(k)).length === 0}>
                      Generate
                    </Button>
                  </Box>
                </Box>
              );
            })())
          : (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mt: 1 }}>
              No data to display.
            </Typography>
          )}
        </Stack>
      </Paper>
      <Notification notify={notify} setNotify={setNotify} />

      <Dialog open={openModal} onClose={handleCloseModal} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            Add Narration Details
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, marginTop: 2 }}>
            <TextField
              label="Credit Narration"
              variant="outlined"
              fullWidth
              value={creditNarration}
              onChange={(e) => setCreditNarration(e.target.value)}
              placeholder="Enter credit narration"
              multiline
              rows={2}
            />
            <TextField
              label="Debit Narration"
              variant="outlined"
              fullWidth
              value={debitNarration}
              onChange={(e) => setDebitNarration(e.target.value)}
              placeholder="Enter debit narration"
              multiline
              rows={2}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ padding: '16px 24px' }}>
          <Button onClick={handleCloseModal} color="error" variant="outlined">
            Cancel
          </Button>
          <Button onClick={handleSave} color="primary" variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default StipendUploadFile;

